/* eslint-disable no-unused-vars */
import { ConfigProvider } from 'antd';
import 'antd/dist/antd.less';
import React, { useEffect, useState, lazy } from 'react';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { BrowserRouter as Router, Navigate, Route, Routes } from 'react-router-dom';

import { ThemeProvider } from 'styled-components';
import ProtectedRoute from './components/utilities/protectedRoute';
import config, { SOCKET_END_POINT } from './config/config';
import store from './redux/store';

import SuperAdminRoutes from './routes/super-admin';
import CompanyRoutes from './routes/company';
import FinanceRoutes from './routes/finance';
import AuthRoutes from './routes/auth';
import './static/css/index.css';
import './static/css/style.css';
import { validateUser } from './redux/authentication/validateUser';
import { connectWebSocket, disconnectWebSocket } from './redux/socket/actions';
import { AdminRoutes, AtsRoutes, HQRoutes, PrintRoutes } from './routes';

const Verification = lazy(() => import('./pages/qr-verification/Verification'));
const NotFound = lazy(() => import('./container/pages/404'));

const { themeColor } = config;

function ProviderConfig() {
  const dispatch = useDispatch();

  const { rtl, authUser, isLoggedIn, topMenu, mainContent } = useSelector((state) => ({
    rtl: state.ChangeLayoutMode.rtlData,
    topMenu: state.ChangeLayoutMode.topMenu,
    mainContent: state.ChangeLayoutMode.mode,
    isLoggedIn: state.auth.login,
    authUser: state.auth.data,
  }));

  const protectedRoutes = {
    's-admin': SuperAdminRoutes,
    'comp': CompanyRoutes,
    'admin': AdminRoutes,
    'finance': FinanceRoutes,
    'ats': AtsRoutes,
    'hq-operator': HQRoutes,
    'p-operator': PrintRoutes,
  };

  useEffect(() => {
    if (authUser) {
      const token = authUser?.access_token;
      dispatch(connectWebSocket(SOCKET_END_POINT, token));
    }
    return () => {
      dispatch(disconnectWebSocket());
    };
  }, [authUser, dispatch]);

  return (
    <ConfigProvider direction={rtl ? 'rtl' : 'ltr'}>
      <ThemeProvider theme={{ ...themeColor, rtl, topMenu, mainContent }}>
        <Router>
          <Routes>
            {/* Public route for QR Code Verification */}
            <Route path="/pubcert/:enc_key" element={<Verification />} />

            {/* Routes for unauthenticated users */}
            {!isLoggedIn ? (
              <Route path="/*" element={<AuthRoutes />} />
            ) : (
              <>
                {/* Protected routes for authenticated users */}
                <Route
                  path={`/${authUser.api_url}/*`}
                  element={<ProtectedRoute path="/*" Component={protectedRoutes[authUser.api_url]} />}
                />
                <Route path="*" element={<NotFound />} />
              </>
            )}

            {/* Redirect from login if already logged in */}
            {isLoggedIn && authUser && window.location.pathname === `/login` && (
              <Route path="/login" element={<Navigate to={`/${authUser.api_url}/dashboard`} />} />
            )}
          </Routes>
        </Router>
      </ThemeProvider>
    </ConfigProvider>
  );
}

function App() {
  useEffect(() => {
    validateUser(); // Validate user session
  }, []);

  return (
    <Provider store={store}>
      <ProviderConfig />
    </Provider>
  );
}

export default App;
